import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import BrandLogo from "@/components/BrandLogo";
import { Heart, ShoppingCart, Trash2, Search, User, ArrowLeft, Package } from "lucide-react";
import { quickAddToCart, getCartCount } from "@/utils/cart";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmt(n) {
  if (!n && n !== 0) return "";
  return Number(n).toLocaleString("en-IN");
}

function discount(mrp, price) {
  if (!mrp || !price || mrp <= price) return 0;
  return Math.round(((mrp - price) / mrp) * 100);
}

// ── Wishlist helpers (localStorage) ──────────────────────────────────────────
export function getWishlist() {
  try { return JSON.parse(localStorage.getItem("it_wishlist") || "[]"); } catch { return []; }
}
export function saveWishlist(ids) {
  localStorage.setItem("it_wishlist", JSON.stringify(ids));
  window.dispatchEvent(new Event("wishlist-updated"));
}
export function isWishlisted(productId) {
  return getWishlist().includes(productId);
}
export function toggleWishlist(productId) {
  const list = getWishlist();
  if (list.includes(productId)) {
    saveWishlist(list.filter(id => id !== productId));
    return false;
  } else {
    saveWishlist([...list, productId]);
    return true;
  }
}
export function getWishlistCount() {
  return getWishlist().length;
}

// ── Header ────────────────────────────────────────────────────────────────────
function PageHeader({ cartCount }) {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const customerUser = (() => {
    try { return JSON.parse(localStorage.getItem("it_customer_user") || "null"); } catch { return null; }
  })();

  return (
    <header className="bg-white border-b border-slate-100 sticky top-0 z-40 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-4">
        <Link to="/" className="flex-shrink-0">
          <BrandLogo size="md" />
        </Link>
        <form onSubmit={e => { e.preventDefault(); if (search.trim()) navigate(`/search?q=${encodeURIComponent(search.trim())}`); }}
          className="flex-1 max-w-2xl hidden sm:flex">
          <div className="flex w-full border-2 border-orange-500 rounded-lg overflow-hidden">
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search for products, brands and more…"
              className="flex-1 px-4 py-2.5 text-sm focus:outline-none text-slate-700" />
            <button type="submit" className="bg-orange-500 hover:bg-orange-600 px-4 transition-colors">
              <Search className="w-4 h-4 text-white" />
            </button>
          </div>
        </form>
        <div className="flex items-center gap-3 ml-auto flex-shrink-0">
          <Link to="/wishlist" className="relative text-orange-500 hidden sm:block">
            <Heart className="w-5 h-5 fill-orange-500" />
          </Link>
          <Link to="/cart" className="relative text-slate-600 hover:text-orange-500 transition-colors">
            <ShoppingCart className="w-5 h-5" />
            {cartCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 bg-orange-500 text-white text-[9px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
                {cartCount}
              </span>
            )}
          </Link>
          {customerUser ? (
            <Link to="/account" className="hidden sm:flex items-center gap-1.5 text-xs text-slate-600 hover:text-orange-500 transition-colors border border-slate-200 hover:border-orange-400 rounded-lg px-2.5 py-1.5">
              <User className="w-3.5 h-3.5" />
              <span className="font-medium">{customerUser.name?.split(" ")[0] || "Account"}</span>
            </Link>
          ) : (
            <Link to="/customer-login" className="hidden sm:flex items-center gap-1.5 text-xs text-slate-600 hover:text-orange-500 transition-colors border border-slate-200 hover:border-orange-400 rounded-lg px-2.5 py-1.5">
              <User className="w-3.5 h-3.5" />
              <span className="font-medium">Sign In</span>
            </Link>
          )}
        </div>
      </div>
    </header>
  );
}

// ── Wishlist Card ─────────────────────────────────────────────────────────────
function WishlistCard({ product, onRemove }) {
  const disc = discount(product.mrp, product.deal_price_incl_gst);

  const handleAddToCart = () => {
    quickAddToCart(product);
    toast.success("Added to cart!");
    window.dispatchEvent(new Event("cart-updated"));
  };

  return (
    <div data-testid={`wishlist-card-${product.id}`}
      className="bg-white rounded-xl border border-slate-100 overflow-hidden flex gap-0 hover:shadow-md transition-all group">
      {/* Image */}
      <Link to={`/products/${product.url_slug || product.id}`}
        className="relative w-32 sm:w-40 flex-shrink-0 bg-slate-50 overflow-hidden">
        <img
          src={product.main_image || "https://images.unsplash.com/photo-1586953208270-d7db5e9ac21e?w=300"}
          alt={product.item_name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          style={{ minHeight: 128 }}
        />
        {disc > 0 && (
          <span className="absolute top-2 left-2 bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">
            -{disc}%
          </span>
        )}
      </Link>

      {/* Details */}
      <div className="flex-1 p-4 flex flex-col justify-between min-w-0">
        <div>
          {product.brand_name && (
            <p className="text-[10px] text-slate-400 font-medium uppercase tracking-wide mb-0.5">{product.brand_name}</p>
          )}
          <Link to={`/products/${product.url_slug || product.id}`}
            className="text-sm font-semibold text-slate-800 leading-snug line-clamp-2 hover:text-orange-600 transition-colors block">
            {product.item_name}
          </Link>
          <div className="flex items-baseline gap-2 mt-2">
            <span className="text-base font-bold text-slate-900">₹{fmt(product.deal_price_incl_gst)}</span>
            {product.mrp > product.deal_price_incl_gst && (
              <span className="text-xs text-slate-400 line-through">₹{fmt(product.mrp)}</span>
            )}
            {disc > 0 && (
              <span className="text-xs text-green-600 font-semibold">{disc}% off</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 mt-3">
          <button
            onClick={handleAddToCart}
            data-testid={`wishlist-add-to-cart-${product.id}`}
            className="flex items-center gap-1.5 px-3 py-2 bg-orange-500 hover:bg-orange-600 text-white text-xs font-bold rounded-lg transition-colors"
          >
            <ShoppingCart className="w-3.5 h-3.5" /> Add to Cart
          </button>
          <button
            onClick={() => onRemove(product.id)}
            data-testid={`wishlist-remove-${product.id}`}
            className="flex items-center gap-1 px-2.5 py-2 text-red-500 hover:bg-red-50 text-xs font-semibold rounded-lg border border-red-200 transition-colors"
          >
            <Trash2 className="w-3.5 h-3.5" /> Remove
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function WishlistPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cartCount, setCartCount] = useState(getCartCount);

  useEffect(() => {
    const update = () => setCartCount(getCartCount());
    window.addEventListener("cart-updated", update);
    return () => window.removeEventListener("cart-updated", update);
  }, []);

  const fetchWishlistProducts = async () => {
    const ids = getWishlist();
    if (!ids.length) { setProducts([]); setLoading(false); return; }
    setLoading(true);
    try {
      const results = await Promise.all(
        ids.map(id =>
          axios.get(`${API}/products/public/${id}`).then(r => r.data).catch(() => null)
        )
      );
      setProducts(results.filter(Boolean));
    } catch {
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchWishlistProducts(); }, []);

  useEffect(() => {
    const handler = () => fetchWishlistProducts();
    window.addEventListener("wishlist-updated", handler);
    return () => window.removeEventListener("wishlist-updated", handler);
  }, []);

  const handleRemove = (productId) => {
    toggleWishlist(productId);
    setProducts(prev => prev.filter(p => p.id !== productId));
    toast.success("Removed from wishlist");
  };

  return (
    <div className="min-h-screen bg-[#f5f7fa]">
      <PageHeader cartCount={cartCount} />

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Title */}
        <div className="flex items-center gap-3 mb-6">
          <Link
            to="/"
            onClick={(e) => { if (window.history.length > 1) { e.preventDefault(); window.history.back(); } }}
            className="text-slate-400 hover:text-slate-600 transition-colors"
            data-testid="wishlist-back"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-xl font-black text-slate-900">My Wishlist</h1>
            {!loading && (
              <p className="text-sm text-slate-500">{products.length} {products.length === 1 ? "item" : "items"}</p>
            )}
          </div>
        </div>

        {loading && (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {!loading && products.length === 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-16 text-center" data-testid="empty-wishlist">
            <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <Heart className="w-8 h-8 text-red-300" />
            </div>
            <h2 className="text-lg font-bold text-slate-800 mb-2">Your wishlist is empty</h2>
            <p className="text-sm text-slate-500 mb-6">Save products you love and come back to them anytime.</p>
            <Link to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white text-sm font-bold rounded-xl transition-colors">
              <Package className="w-4 h-4" /> Browse Products
            </Link>
          </div>
        )}

        {!loading && products.length > 0 && (
          <div className="space-y-3" data-testid="wishlist-items">
            {products.map(product => (
              <WishlistCard key={product.id} product={product} onRemove={handleRemove} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
